import { useApi } from '~/api'
import { defineService } from '~/api/models/helpers'
import type { WithId } from '~/types/common'
import type { PaginationParams } from '~/types/pagination'
import type { AlertRule } from '~/types/alert'
import type { AuthArgs } from '~/api/models/index'

const queries = {
  getAll: {
    queryFn: (args: Ref<PaginationParams>) => useApi().alerts.getAllAlertRules(args.value),
    queryKey: (args: Ref<PaginationParams>) => [
      'allAlertRules',
      {
        current_page: args.value.current_page,
        per_page: args.value.per_page,
        sort: args.value.sort
      }
    ]
  },
  getById: {
    queryFn: (args: { id: number }) => useApi().alerts.getAlertRuleById(args.id),
    queryKey: (args: { id: number }) => ['alertRule', String(args.id)],
    options: {
      staleTime: () => 0, //TODO: revisit this decision
      refetchOnMount: (): 'always' => 'always' //TODO: revisit this decision
    }
  },
  getAlertsByRuleId: {
    queryFn: (params: { ruleId: number; pagination: PaginationParams }) =>
      useApi().alerts.getAlertsByRuleId(params.ruleId, params.pagination),
    queryKey: (params: { ruleId: number; pagination: PaginationParams }) => [
      'alertsByRule',
      String(params.ruleId),
      String(params.pagination)
    ]
  }
} as const

const mutations = {
  create: {
    request: (payload: Omit<AlertRule, 'id'>) => useApi().alerts.createAlertRule(payload),
    cacheKey: () => ['allAlertRules']
  },
  update: {
    request: ({ id, ...payload }: WithId<AlertRule>) => useApi().alerts.updateAlertRule(id, payload),
    cacheKey: ({ id }: WithId<Partial<AlertRule>>) => [['allAlertRules'], ['alertRule', String(id)]]
  },
  delete: {
    request: (id: number) => useApi().alerts.deleteAlertRule(id),
    cacheKey: ({ id }: WithId<Partial<AlertRule>>) => [['allAlertRules'], ['alertRule', String(id)]]
  },
  toggleStatus: {
    request: (rule: AlertRule) => useApi().alerts.updateAlertRule(rule.id, rule),
    cacheKey: (rule: AlertRule) => [['allAlertRules'], ['alertRule', String(rule.id)]]
  }
} as const

export type AlertRuleService = ReturnType<typeof defineService<typeof queries, typeof mutations>>

export const registerService = (authArgs: AuthArgs): AlertRuleService =>
  defineService(authArgs, {
    queries,
    mutations
  })
